import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, apiFetch } from '../lib/api';
import { fmt12, todayStr, WeatherIcon } from '../components/ui';
import { localizeEventTime } from '../lib/tz';
import { groupEventsByZone, formatInstantInZone } from '../lib/zoneBands';
import { useAuth } from '../hooks/useAuth';
import { usePeople } from '../context/PeopleContext';
import TravelModal from '../components/TravelModal';
import EventModal from '../components/EventModal';
import LodgingPanel from '../components/LodgingPanel';
import { getTravelHero } from '../lib/eventHero';
import { isPushSupported, isPushSubscribed, subscribeToPush } from '../lib/pushNotifications';

function isEventPast(event, sheetDate) {
  const endTime = event.timeEnd || event.timeStart;
  if (!endTime) return false;
  const now = new Date();
  const [h, m] = endTime.split(':').map(Number);
  const eventEnd = sheetDate ? new Date(sheetDate + 'T00:00:00') : new Date();
  eventEnd.setHours(h, m, 0, 0);
  return now > eventEnd;
}

/* ── Styled Event Row ── */
function EventRow({ event, sheetDate, colorMap, onTap, displayZone, suppressBadge }) {
  const past = isEventPast(event, sheetDate);
  const persons = (event.person||'').split(',').map(p=>p.trim()).filter(p => p && p.toLowerCase() !== 'everyone');
  const personColors = persons.map(p => {
    const lower = p.toLowerCase();
    const key = Object.keys(colorMap).find(k => k.toLowerCase() === lower)
      || Object.keys(colorMap).find(k => k.toLowerCase().split(' ')[0] === lower.split(' ')[0]);
    return key ? colorMap[key] : null;
  }).filter(Boolean);
  const pc = personColors[0] || null;
  const artistColor = event._artistColor || null;
  const barStyle = artistColor
    ? { background: artistColor }
    : personColors.length > 1
      ? { background: `linear-gradient(to bottom, ${personColors.join(', ')})` }
      : { background: pc || 'var(--border2)' };
  const titleColor = artistColor || pc || 'var(--text)';

  const isZoom = event.url?.includes('zoom');
  const isMeet = event.url?.includes('meet.google');
  const isTeams = event.url?.includes('teams');
  const hasLink = isZoom || isMeet || isTeams;
  const linkLabel = isZoom ? 'Zoom' : isMeet ? 'Meet' : isTeams ? 'Teams' : '';
  const travelBadge = { flight:{label:'Flight',icon:'fa-plane'}, hotel:{label:'Hotel',icon:'fa-hotel'}, carservice:{label:'Car',icon:'fa-car-side'}, car:{label:'Car',icon:'fa-car'}, ground:{label:'Ground',icon:'fa-van-shuttle'}, train:{label:'Train',icon:'fa-train'} }[event.travelType];

  return (
    <div onClick={() => onTap && onTap(event)}
      style={{ background:'var(--card)', borderRadius:12, border:'1px solid var(--border)', marginBottom:8, cursor: onTap ? 'pointer' : 'default', overflow:'hidden', transition:'all 0.15s',
        opacity: past ? 0.4 : 1 }}
      onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.03)'}
      onMouseLeave={e => e.currentTarget.style.background='var(--card)'}>
      <div style={{ display:'flex', alignItems:'stretch' }}>
        <div style={{ width:4, flexShrink:0, ...barStyle }} />
        <div style={{ flex:1, padding:'12px 16px' }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
            <p style={{ fontSize:16, fontWeight:800, color: titleColor, margin:0, lineHeight:1.4 }}>
              {event.timeStart && (() => {
                const sheetTz = event._sheet?.timezone;
                let localTime, sourceLabel;
                if (suppressBadge && event.startInstant && displayZone) {
                  // Banded row → render in the band's zone, no badge.
                  localTime = formatInstantInZone(event.startInstant, displayZone);
                  sourceLabel = null;
                } else {
                  const r = localizeEventTime(event, sheetTz, fmt12);
                  localTime = r.localTime;
                  sourceLabel = r.sourceLabel;
                }
                return (
                  <>
                    <span style={{ textDecoration:'underline' }}>{fmt12(localTime || event.timeStart)}</span>
                    {sourceLabel && <span style={{ fontSize:10, fontWeight:700, color:'#b45309', background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.4)', borderRadius:8, padding:'1px 6px', marginLeft:6, whiteSpace:'nowrap' }}>{sourceLabel}</span>}
                    <span>: </span>
                  </>
                );
              })()}
              {persons.length > 0 && <>{persons[0]} </>}
              {hasLink
                ? <a href={event.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color:titleColor, textDecoration:'underline', textDecorationColor: titleColor+'66' }}>{event.title}</a>
                : event.title}
            </p>
            <div style={{ display:'flex', gap:4, flexShrink:0, alignItems:'center' }}>
              {hasLink && (
                <a href={event.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                  style={{ fontSize:10, fontWeight:700, color:'var(--blue)', background:'rgba(30,64,175,0.1)', border:'1px solid rgba(30,64,175,0.2)', borderRadius:6, padding:'2px 8px', textDecoration:'none' }}>
                  <i className={`fa-solid ${isZoom ? 'fa-video' : isMeet ? 'fa-video' : 'fa-headset'}`} style={{ marginRight:3, fontSize:8 }}></i>
                  {linkLabel}
                </a>
              )}
              {travelBadge && (
                <span style={{ fontSize:10, fontWeight:700, color:'var(--blue)', background:'rgba(30,64,175,0.1)', border:'1px solid rgba(30,64,175,0.2)', borderRadius:6, padding:'2px 8px' }}>
                  <i className={`fa-solid ${travelBadge.icon}`} style={{ marginRight:3, fontSize:8 }}></i>
                  {travelBadge.label}
                </span>
              )}
              {persons.length > 1 && (
                <span style={{ fontSize:10, fontWeight:700, color:pc, background:pc+'22', border:'1px solid '+pc+'44', borderRadius:20, padding:'1px 7px' }}>+{persons.length}</span>
              )}
              {event.files?.length > 0 && (
                <span style={{ fontSize:10, color:'var(--text3)' }}><i className="fa-solid fa-paperclip"></i></span>
              )}
            </div>
          </div>
          {event.location && (
            <a href={`https://maps.apple.com/?q=${encodeURIComponent(event.location)}`} onClick={e => e.stopPropagation()}
              style={{ fontSize:11, color:'var(--blue)', textDecoration:'none', display:'block', marginTop:3 }}>
              <i className="fa-solid fa-location-dot" style={{ fontSize:9, marginRight:4 }}></i>
              {event.location.length > 50 ? event.location.substring(0,50)+'...' : event.location}
            </a>
          )}
          {event.notes && (() => {
            const text = event.notes.length > 120 ? event.notes.substring(0,120)+'...' : event.notes;
            const phoneRe = /(\+?1?[\s\-.]?\(?\d{3}\)?[\s\-.]?\d{3}[\s\-.]?\d{4}|\d{10,11})/g;
            const parts = []; let last = 0; let m;
            while ((m = phoneRe.exec(text)) !== null) {
              if (m.index > last) parts.push({ t:'text', v: text.slice(last, m.index) });
              parts.push({ t:'phone', v: m[0] });
              last = m.index + m[0].length;
            }
            if (last < text.length) parts.push({ t:'text', v: text.slice(last) });
            return (
              <p style={{ fontSize:11, color:'var(--text3)', marginTop:4, lineHeight:1.5, whiteSpace:'pre-wrap' }}>
                {parts.length ? parts.map((p, i) => p.t === 'phone' ? (
                  <a key={i} href={`tel:${p.v.replace(/\D/g,'')}`} onClick={e => e.stopPropagation()}
                    style={{ color:'#22c55e', textDecoration:'none', fontWeight:600 }}>
                    <i className="fa-solid fa-phone" style={{ fontSize:9, marginRight:2 }}></i>{p.v}
                  </a>
                ) : <span key={i}>{p.v}</span>) : text}
              </p>
            );
          })()}
          {event.passengers && (
            <p style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>
              <span style={{ fontWeight:700, color:'var(--text2)' }}>Additional Passengers:</span> {event.passengers}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Weather Pill ── */
function WeatherPill({ weather, location }) {
  if (!weather) return null;
  return (
    <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:12, padding:'8px 14px' }}>
      <WeatherIcon condition={weather.condition} size={16} />
      <span style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{weather.tempHigh}° / {weather.tempLow}°</span>
      <span style={{ fontSize:11, color:'var(--text3)' }}>{weather.condition}</span>
      {location && <span style={{ fontSize:10, color:'var(--text3)', opacity:0.6 }}>{location}</span>}
    </div>
  );
}

export default function ViewerPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const today = todayStr();
  const { colorMap } = usePeople();

  const [artists,      setArtists]      = useState([]);
  const [selArtist,    setSelArtist]    = useState(null);
  const [showAll,      setShowAll]      = useState(false);
  const [allSheets,    setAllSheets]    = useState([]);
  const [fullSheet,    setFullSheet]    = useState(null);
  const [loadingSheet, setLoadingSheet] = useState(true);
  const [initialized,  setInitialized]  = useState(false);
  const [travelModal,  setTravelModal]  = useState(null);
  const [eventModal,   setEventModal]   = useState(null);
  const [sheets,       setSheets]       = useState([]);
  const [selDate,      setSelDate]      = useState(today);
  const [touchStart,   setTouchStart]   = useState(null);
  const [lastUpdated,  setLastUpdated]  = useState(null);
  const [showPushBanner, setShowPushBanner] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [codeValue, setCodeValue] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeMsg, setCodeMsg] = useState(null); // { type: 'success'|'error', text }
  const [approvalCount, setApprovalCount] = useState(0);

  const isApprovalsUser = user?.role === 'admin' || user?.canApprove;

  useEffect(() => {
    if (!isApprovalsUser) return;
    let alive = true;
    const load = () => apiFetch('/approvals/pending-count')
      .then(r => { if (alive) setApprovalCount(r?.count || 0); })
      .catch(() => {});
    load();
    const id = setInterval(load, 30_000);
    return () => { alive = false; clearInterval(id); };
  }, [isApprovalsUser]);

  const handleRedeemCode = async () => {
    if (!codeValue.trim()) return;
    setCodeLoading(true); setCodeMsg(null);
    try {
      const result = await apiFetch('/auth/redeem-code', { method: 'POST', body: JSON.stringify({ code: codeValue.trim() }) });
      setCodeMsg({ type: 'success', text: `Joined ${result.artist?.name || 'team'}!` });
      setCodeValue('');
      // Refresh artists list
      apiFetch('/artists').then(data => {
        const list = data.artists || data || [];
        setArtists(list);
        if (list.length && !selArtist) setSelArtist(list[0].id);
      }).catch(() => {});
      setTimeout(() => { setShowCodeInput(false); setCodeMsg(null); }, 1500);
    } catch (e) {
      setCodeMsg({ type: 'error', text: e.message || 'Invalid code' });
    } finally { setCodeLoading(false); }
  };

  // Push notification opt-in banner
  useEffect(() => {
    if (!isPushSupported()) return;
    if (localStorage.getItem('ds_push_prompted')) return;
    isPushSubscribed().then(sub => { if (!sub) setShowPushBanner(true); });
  }, []);
  const handlePushAllow = async () => {
    try { await subscribeToPush(); } catch {}
    setShowPushBanner(false);
    localStorage.setItem('ds_push_prompted', '1');
  };
  const handlePushDismiss = () => {
    setShowPushBanner(false);
    localStorage.setItem('ds_push_prompted', '1');
  };

  const prevDay = () => { const d = new Date(selDate+'T12:00:00'); d.setDate(d.getDate()-1); setSelDate(d.toISOString().slice(0,10)); };
  const nextDay = () => { const d = new Date(selDate+'T12:00:00'); d.setDate(d.getDate()+1); setSelDate(d.toISOString().slice(0,10)); };
  const goToday = () => setSelDate(today);

  useEffect(() => {
    apiFetch('/artists').then(d => {
      const list = d.artists || d || [];
      setArtists(list);
      if (list.length) setSelArtist(list[0].id);
      else setLoadingSheet(false);
    }).catch(() => setLoadingSheet(false));
  }, []);

  useEffect(() => {
    if (showAll) return;
    if (!selArtist) { setLoadingSheet(false); return; }
    api.getSheets(selArtist).then(d => {
      const all = d.sheets || d || [];
      setSheets(all.filter(s => s.status === 'published'));
    }).catch(() => {});
  }, [selArtist, showAll]);

  useEffect(() => {
    if (!showAll || !artists.length) return;
    setLoadingSheet(true);
    Promise.all(artists.map(async a => {
      try {
        const d = await api.getSheets(a.id);
        const published = (d.sheets || d || []).filter(s => s.status === 'published');
        const todaySheet = published.find(s => s.date === selDate);
        if (!todaySheet) return null;
        const full = await apiFetch('/sheets/' + todaySheet.id);
        return { ...full, artistInfo: a };
      } catch { return null; }
    })).then(results => {
      setAllSheets(results.filter(Boolean));
      setLoadingSheet(false);
      setInitialized(true);
    });
  }, [showAll, artists, selDate]);

  useEffect(() => {
    if (showAll) return;
    const stub = sheets.find(s => s.date === selDate);
    if (!stub) { setFullSheet(null); setLoadingSheet(false); setInitialized(true); return; }
    setLoadingSheet(true);
    apiFetch('/sheets/' + stub.id)
      .then(data => setFullSheet(data))
      .catch(() => setFullSheet(null))
      .finally(() => { setLoadingSheet(false); setInitialized(true); });
  }, [sheets, showAll, selDate]);

  const artist = fullSheet?.artist || artists.find(a => a.id === selArtist);
  const handleLogout = () => { logout(); navigate('/login'); };

  // Poll for updates every 15 seconds
  useEffect(() => {
    if (!fullSheet?.id) return;
    const sheetId = fullSheet.id;
    const interval = setInterval(() => {
      apiFetch('/sheets/' + sheetId).then(data => {
        setFullSheet(prev => {
          if (!prev) return data;
          const prevIds = (prev.events||[]).map(e=>e.id).sort().join(',');
          const newIds = (data.events||[]).map(e=>e.id).sort().join(',');
          const changed = prev.updatedAt !== data.updatedAt || prevIds !== newIds || prev.notes !== data.notes;
          if (changed) {
            setLastUpdated(new Date());
            if (Notification.permission === 'granted') {
              new Notification('Day Sheet Updated', { body: `${data.artist?.name||'Day Sheet'} has been updated`, icon: '/icon.png' });
            }
            return data;
          }
          return prev;
        });
      }).catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, [fullSheet?.id]);

  // Request notification permission on first load
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      // Delay the prompt so it doesn't pop up immediately
      const timer = setTimeout(() => Notification.requestPermission(), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Check for newly published sheets (polling all sheets)
  useEffect(() => {
    if (!selArtist) return;
    const interval = setInterval(() => {
      api.getSheets(selArtist).then(d => {
        const published = (d.sheets || d || []).filter(s => s.status === 'published');
        const dateSheet = published.find(s => s.date === selDate);
        if (dateSheet && !fullSheet) {
          // A sheet was just published for this date
          setSheets(published);
          if (Notification.permission === 'granted') {
            new Notification('Day Sheet Ready!', {
              body: `Your day sheet for ${selDate} is now available`,
              icon: '/icon.png',
            });
          }
        }
      }).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [selArtist, selDate, fullSheet]);

  const dateStr = new Date(selDate+'T12:00:00').toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'});

  /* ── Render helpers ── */
  const renderHeader = (name, city, weatherData, locationWeathers) => {
    const weathers = [];
    if (weatherData) weathers.push({ location: city?.replace(', USA','').split(',')[0].trim(), weather: weatherData });
    (locationWeathers||[]).forEach(lw => weathers.push(lw));

    return (
      <div style={{ padding:'32px 20px 24px', textAlign:'center', position:'relative' }}>
        {/* Subtle glow */}
        <div style={{ position:'absolute', top:0, left:'50%', transform:'translateX(-50%)', width:300, height:200, background:'radial-gradient(ellipse, rgba(30,64,175,0.06), transparent 70%)', pointerEvents:'none' }} />
        <h1 style={{ fontSize:26, fontWeight:900, color:'var(--text)', margin:'0 0 4px', letterSpacing:-1, position:'relative' }}>
          {name ? `${name} Day Sheet` : 'Day Sheet'}
        </h1>
        <p style={{ fontSize:15, color:'var(--text2)', fontWeight:400, position:'relative' }}>{dateStr}</p>
        {city && (
          <a href={`https://maps.apple.com/?q=${encodeURIComponent(city)}`} target="_blank" rel="noreferrer"
            style={{ fontSize:12, color:'var(--text3)', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:4, marginTop:6, position:'relative' }}>
            <i className="fa-solid fa-location-dot" style={{ fontSize:9 }}></i>
            {city.replace(', USA','')}
          </a>
        )}
        {weathers.length > 0 && (
          <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:12, flexWrap:'wrap', position:'relative' }}>
            {weathers.map((lw, i) => <WeatherPill key={i} weather={lw.weather} location={weathers.length > 1 ? lw.location : null} />)}
          </div>
        )}
      </div>
    );
  };

  const renderNotes = (text, label) => {
    if (!text) return null;
    return (
      <div style={{ margin:'0 16px 12px', background:'rgba(251,191,36,0.04)', borderLeft:'3px solid #fbbf24', borderRadius:'0 14px 14px 0', padding:'14px 18px' }}>
        <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'#fbbf24', marginBottom:6, textTransform:'uppercase' }}>
          <i className="fa-solid fa-thumbtack" style={{ marginRight:4, fontSize:9 }}></i>
          {label || 'Note'}
        </p>
        <p style={{ fontSize:14, color:'var(--text2)', lineHeight:1.7, margin:0 }}>{text}</p>
      </div>
    );
  };

  const openEventModal = (ev, events, modalSheet) => {
    const sorted = [...events].sort((a,b) => (a.timeStart||'99:99').localeCompare(b.timeStart||'99:99'));
    const idx = sorted.findIndex(e => e.id === ev.id);
    setEventModal({
      event: ev,
      sheet: modalSheet || ev._sheet,
      prevEvent: idx > 0 ? sorted[idx-1] : null,
      nextEvent: idx < sorted.length-1 ? sorted[idx+1] : null,
    });
  };

  const renderSchedule = (events, legend, scheduleSheet) => {
    if (events.length === 0) {
      return (
        <div style={{ margin:'0 16px 12px' }}>
          <div style={{ background:'var(--card)', borderRadius:12, border:'1px solid var(--border)', padding:'32px 0', textAlign:'center' }}>
            <p style={{ color:'var(--text3)', fontSize:14, margin:0 }}>No events scheduled</p>
          </div>
        </div>
      );
    }
    // Group into zone bands so multi-tz days surface city/short-code
    // headers between sections. Today + nextDay halves are grouped
    // separately so bands never cross the work-day boundary.
    const sheetTz = scheduleSheet?.timezone;
    const today = events.filter(e => !e.nextDay);
    const nextDay = events.filter(e => e.nextDay);
    const todaySections = groupEventsByZone(today, sheetTz);
    const nextDaySections = groupEventsByZone(nextDay, sheetTz);
    const renderBandHeader = (label, short, key) => (
      <div key={key} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', margin:'4px 0' }}>
        <div style={{ flex:1, height:1, background:'var(--border)' }} />
        <span style={{
          fontSize:11, fontWeight:800, letterSpacing:'0.10em', textTransform:'uppercase',
          padding:'4px 10px', borderRadius:999,
          background:'rgba(30,64,175,0.10)', whiteSpace:'nowrap',
        }}>
          <span style={{ color:'var(--text3)' }}>{label}{' · '}</span>
          <span style={{ color:'var(--blue)', fontWeight:800 }}>{short || ''}</span>
        </span>
        <div style={{ flex:1, height:1, background:'var(--border)' }} />
      </div>
    );
    const renderSections = (sections, prefix) => sections.flatMap((sec, si) => {
      const items = [];
      if (sec.kind === 'band') items.push(renderBandHeader(sec.label, sec.short, `${prefix}-band-${si}`));
      sec.events.forEach(e => {
        items.push(
          <EventRow
            key={e.id}
            event={e}
            sheetDate={selDate}
            colorMap={colorMap}
            displayZone={sec.kind === 'band' ? sec.zone : undefined}
            suppressBadge={sec.kind === 'band'}
            onTap={(ev) => openEventModal(ev, events, scheduleSheet)}
          />
        );
      });
      return items;
    });
    return (
      <div style={{ margin:'0 16px 12px' }}>
        {renderSections(todaySections, 'td')}
        {nextDay.length > 0 && (
          <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 16px', margin:'4px 0' }}>
            <div style={{ flex:1, height:1, background:'linear-gradient(to right, var(--border), transparent)' }} />
            <span style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)' }}>NEXT DAY</span>
            <div style={{ flex:1, height:1, background:'linear-gradient(to left, var(--border), transparent)' }} />
          </div>
        )}
        {renderSections(nextDaySections, 'nd')}
      </div>
    );
  };

  const renderPersonLegend = (events) => {
    const allPersons = new Map();
    events.forEach(e => {
      (e.person||'').split(',').map(p=>p.trim()).filter(Boolean).forEach(p => {
        if (!allPersons.has(p.toLowerCase())) {
          const key = Object.keys(colorMap).find(k => k.toLowerCase() === p.toLowerCase());
          const color = key ? colorMap[key] : null;
          if (color) allPersons.set(p.toLowerCase(), { name: p, color });
        }
      });
    });
    const people = [...allPersons.values()];
    if (!people.length) return null;
    return (
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        {people.map((p,i) => (
          <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:10, fontWeight:700, color: p.color }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background: p.color }} />
            {p.name.split(' ').map(w=>w[0]).join('')}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', maxWidth:480, margin:'0 auto', paddingTop:'env(safe-area-inset-top)', paddingBottom:'calc(60px + env(safe-area-inset-bottom))' }}>

      {/* ── Top bar ── */}
      <div style={{ padding:'10px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid var(--border)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
          {(user?.role === 'admin') && (
            <button onClick={() => navigate('/admin')}
              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--blue)', fontSize:14, padding:'4px 2px', display:'flex', alignItems:'center', gap:4 }}>
              <i className="fa-solid fa-chevron-left" style={{ fontSize:11 }}></i>
            </button>
          )}
          <img src="/icon.png" alt="" style={{ width:24, height:24, borderRadius:6 }} />
          <span style={{ fontSize:15, fontWeight:800, color:'var(--text)', letterSpacing:-0.3 }}>Day Sheets</span>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {isApprovalsUser && (
            <button onClick={() => navigate(user?.role === 'admin' ? '/admin/approvals' : '/approvals')}
              style={{ position:'relative', background:'none', border:'none', cursor:'pointer', color: approvalCount > 0 ? 'var(--blue)' : 'var(--text3)', fontSize:14, padding:4 }}
              title={approvalCount > 0 ? `${approvalCount} awaiting your approval` : 'Approvals'}>
              <i className="fa-solid fa-circle-check"></i>
              {approvalCount > 0 && (
                <span style={{ position:'absolute', top:-2, right:-4, minWidth:15, height:15, borderRadius:8, background:'#ef4444', color:'#fff', fontSize:9, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 4px', lineHeight:1, boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }}>
                  {approvalCount > 9 ? '9+' : approvalCount}
                </span>
              )}
            </button>
          )}
          <button onClick={() => { setShowCodeInput(v => !v); setCodeValue(''); setCodeMsg(null); }}
            style={{ background:'none', border:'none', cursor:'pointer', color: showCodeInput ? 'var(--blue)' : 'var(--text3)', fontSize:14, padding:4 }}
            title="Enter access code">
            <i className="fa-solid fa-key"></i>
          </button>
          <button onClick={() => {
            const current = document.documentElement.getAttribute('data-theme');
            const next = current === 'light' ? '' : 'light';
            document.documentElement.setAttribute('data-theme', next);
            localStorage.setItem('ds_theme', next);
          }} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:14, padding:4 }}>
            <i className="fa-solid fa-circle-half-stroke"></i>
          </button>
          {fullSheet && (
            <button onClick={() => {
              const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
              window.open(`${API}/email/pdf/${fullSheet.id}?token=${localStorage.getItem('ds_token')}`, '_blank');
            }} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:14, padding:4 }}>
              <i className="fa-solid fa-file-pdf"></i>
            </button>
          )}
          <button onClick={handleLogout}
            style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:14, padding:4 }}>
            <i className="fa-solid fa-arrow-right-from-bracket"></i>
          </button>
        </div>
      </div>

      {/* ── Access code input panel ── */}
      {showCodeInput && (
        <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', background:'rgba(30,64,175,0.04)' }}>
          <p style={{ fontSize:13, fontWeight:700, color:'var(--text)', margin:'0 0 8px' }}>
            <i className="fa-solid fa-key" style={{ marginRight:6, color:'#f59e0b', fontSize:11 }}></i>
            Enter Access Code
          </p>
          <div style={{ display:'flex', gap:8 }}>
            <input
              value={codeValue}
              onChange={e => setCodeValue(e.target.value.toUpperCase())}
              placeholder="ENTER CODE"
              maxLength={20}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleRedeemCode()}
              style={{
                flex:1, padding:'10px 14px', borderRadius:10, background:'var(--card)', border:'2px solid var(--border)',
                color:'var(--text)', fontFamily:'inherit', fontSize:15, fontWeight:700, letterSpacing:2,
                textTransform:'uppercase', outline:'none', boxSizing:'border-box'
              }}
              onFocus={e => e.target.style.borderColor = '#1E40AF'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
            <button onClick={handleRedeemCode} disabled={codeLoading || !codeValue.trim()}
              style={{
                padding:'10px 18px', borderRadius:10,
                background: codeValue.trim() ? 'var(--blue)' : 'var(--card)',
                border: codeValue.trim() ? 'none' : '1px solid var(--border)',
                color: codeValue.trim() ? '#fff' : 'var(--text3)',
                fontFamily:'inherit', fontSize:14, fontWeight:800, cursor: codeValue.trim() ? 'pointer' : 'default',
                flexShrink:0
              }}>
              {codeLoading ? <i className="fa-solid fa-spinner fa-spin"></i> : 'Join'}
            </button>
          </div>
          {codeMsg && (
            <p style={{ fontSize:12, fontWeight:600, margin:'8px 0 0',
              color: codeMsg.type === 'success' ? '#22c55e' : '#ef4444' }}>
              <i className={`fa-solid ${codeMsg.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} style={{ marginRight:4 }}></i>
              {codeMsg.text}
            </p>
          )}
        </div>
      )}

      {/* ── Push notification opt-in banner ── */}
      {showPushBanner && (
        <div style={{ margin:'8px 12px', padding:'12px 16px', borderRadius:12, background:'rgba(30,64,175,0.08)', border:'1px solid rgba(30,64,175,0.2)', display:'flex', alignItems:'center', gap:12 }}>
          <i className="fa-solid fa-bell" style={{ fontSize:16, color:'var(--blue)', flexShrink:0 }}></i>
          <div style={{ flex:1 }}>
            <p style={{ fontSize:13, fontWeight:700, color:'var(--text)', margin:0 }}>Get schedule updates</p>
            <p style={{ fontSize:11, color:'var(--text3)', margin:'2px 0 0' }}>Enable push notifications</p>
          </div>
          <button onClick={handlePushAllow}
            style={{ padding:'6px 14px', borderRadius:8, background:'var(--blue)', border:'none', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>
            Allow
          </button>
          <button onClick={handlePushDismiss}
            style={{ background:'none', border:'none', color:'var(--text3)', cursor:'pointer', fontSize:14, padding:4 }}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
      )}

      {/* ── Artist selector circles ── */}
      {artists.length > 0 && (
        <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', gap:16, overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
          {artists.length > 1 && (
            <button onClick={() => setShowAll(true)}
              style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, background:'none', border:'none', cursor:'pointer', padding:0, flexShrink:0, minWidth:68 }}>
              <div style={{ width:60, height:60, borderRadius:'50%', background: showAll ? 'rgba(30,64,175,0.15)' : 'var(--bg)', border: showAll ? '2.5px solid var(--blue)' : '2px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <i className="fa-solid fa-layer-group" style={{ fontSize:16, color: showAll ? 'var(--blue)' : 'var(--text3)' }}></i>
              </div>
              <span style={{ fontSize:11, fontWeight:700, color: showAll ? 'var(--blue)' : 'var(--text3)' }}>All</span>
            </button>
          )}
          {artists.map(a => {
            const active = !showAll && selArtist === a.id;
            return (
              <button key={a.id} onClick={() => { setShowAll(false); setSelArtist(a.id); }}
                style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, background:'none', border:'none', cursor:'pointer', padding:0, flexShrink:0, minWidth:68 }}>
                <div style={{ width:60, height:60, borderRadius:'50%', background:`${a.color || 'var(--blue)'}20`, border:`2.5px solid ${active ? (a.color || 'var(--blue)') : 'transparent'}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, overflow:'hidden' }}>
                  {a.photo ? <img src={a.photo} alt={a.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji || '🎵')}
                </div>
                <span style={{ fontSize:11, fontWeight:700, color: active ? 'var(--text)' : 'var(--text3)', maxWidth:68, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name?.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Date navigation */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:16, padding:'12px 16px', borderBottom:'1px solid var(--border)' }}
        onTouchStart={e => setTouchStart(e.touches[0].clientX)}
        onTouchEnd={e => {
          if (touchStart === null) return;
          const diff = e.changedTouches[0].clientX - touchStart;
          if (diff > 60) prevDay();
          else if (diff < -60) nextDay();
          setTouchStart(null);
        }}>
        <button onClick={prevDay} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text2)', fontSize:16, padding:'6px 10px' }}>
          <i className="fa-solid fa-chevron-left"></i>
        </button>
        <div style={{ textAlign:'center', flex:1 }}>
          <p style={{ fontSize:15, fontWeight:700, color:'var(--text)', margin:0 }}>
            {new Date(selDate+'T12:00:00').toLocaleDateString('en-US',{weekday:'long',month:'short',day:'numeric'})}
          </p>
          {selDate !== today && (
            <button onClick={goToday} style={{ fontSize:11, color:'var(--blue)', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', fontWeight:600, marginTop:2 }}>
              Back to today
            </button>
          )}
        </div>
        <button onClick={nextDay} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text2)', fontSize:16, padding:'6px 10px' }}>
          <i className="fa-solid fa-chevron-right"></i>
        </button>
      </div>

      {/* ── Content ── */}
      {loadingSheet ? (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'80px 0' }}>
          <div style={{ width:28, height:28, border:'2.5px solid var(--border)', borderTop:'2.5px solid var(--blue)', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
        </div>

      ) : showAll ? (
        /* ── All Artists merged timeline ── */
        (() => {
          const mergedEvents = [];
          const notes = [];
          const cities = [];
          const weathers = [];
          allSheets.forEach(sheet => {
            const a = sheet.artistInfo || sheet.artist;
            if (sheet.notes) notes.push({ artist: a, notes: sheet.notes });
            if (sheet.city) cities.push(sheet.city.replace(', USA',''));
            if (sheet.weather) weathers.push({ location: sheet.city?.replace(', USA','').split(',')[0].trim(), weather: sheet.weather });
            (sheet.events||[]).forEach(e => {
              mergedEvents.push({ ...e, _artistColor: a?.color, artistName: a?.name, _sheetId: sheet.id, _sheet: sheet });
            });
          });
          mergedEvents.sort((a,b) => (a.timeStart||'99:99').localeCompare(b.timeStart||'99:99'));

          if (!allSheets.length) return (
            <div style={{ textAlign:'center', padding:'80px 24px' }}>
              <i className="fa-regular fa-calendar-xmark" style={{ fontSize:40, color:'var(--text3)', marginBottom:16, display:'block' }}></i>
              <p style={{ fontSize:18, fontWeight:800, color:'var(--text)', marginBottom:6 }}>No sheets for today</p>
              <p style={{ fontSize:13, color:'var(--text3)' }}>Check back later</p>
            </div>
          );

          const legend = (
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              {allSheets.map(s => {
                const a = s.artistInfo || s.artist;
                const initials = (a?.name||'').split(' ').map(w=>w[0]).join('');
                return (
                  <span key={s.id} style={{ display:'inline-flex', alignItems:'center', gap:3, fontSize:10, fontWeight:700, color: a?.color || 'var(--text3)' }}>
                    <span style={{ width:6, height:6, borderRadius:'50%', background: a?.color || 'var(--text3)' }} />
                    {initials}
                  </span>
                );
              })}
            </div>
          );

          const allHotels = allSheets.flatMap(s => s.hotels || []);
          return (
            <>
              {renderHeader('Day Sheet', [...new Set(cities)].join(' / '), weathers[0]?.weather, weathers.length > 1 ? weathers.slice(1) : [])}
              {notes.map((n, i) => renderNotes(n.notes, n.artist?.name))}
              {allHotels.length > 0 && (
                <div style={{ padding:'0 16px 12px' }}>
                  <LodgingPanel hotels={allHotels} />
                </div>
              )}
              {renderSchedule(mergedEvents, legend)}
            </>
          );
        })()

      ) : fullSheet ? (
        /* ── Single artist view ── */
        <>
          {renderHeader(artist?.name, fullSheet.city, fullSheet.weather, fullSheet.locationWeathers)}

          {/* Travel quick buttons */}
          {(fullSheet.travel||[]).length > 0 && (() => {
            const typeIcons = { flight:'fa-plane', hotel:'fa-hotel', carservice:'fa-car-side', car:'fa-car', ground:'fa-van-shuttle', train:'fa-train' };
            const typeColors = { flight:'#1E40AF', hotel:'#06B6D4', carservice:'#06B6D4', car:'#fbbf24', ground:'#f472b6', train:'#a78bfa' };
            const resolveColor = (name) => {
              if (!name) return null;
              const key = Object.keys(colorMap).find(k => k.toLowerCase() === name.toLowerCase());
              return key ? colorMap[key] : null;
            };
            // Group by type + passenger
            const groups = {};
            (fullSheet.travel||[]).forEach(r => {
              const names = (r.passenger||'').split(',').map(s=>s.trim().toLowerCase()).filter(Boolean).sort().join(',');
              const key = `${r.type}::${names}`;
              if (!groups[key]) groups[key] = [];
              groups[key].push(r);
            });
            const getSummary = (r) => {
              if (r.type === 'flight') return `${r.departureAirport||'?'} → ${r.arrivalAirport||'?'}`;
              if (r.type === 'hotel') return (r.hotelName||'Hotel').split(' ').slice(0,3).join(' ');
              if (r.type === 'carservice') return [r.carServicePickup, r.carServiceDropoff].filter(Boolean).map(s=>s.split(',')[0].split(' ').slice(0,2).join(' ')).join(' → ') || 'Car Service';
              if (r.type === 'ground') return [r.pickupLocation, r.dropoffLocation].filter(Boolean).map(s=>s.split(',')[0].split(' ').slice(0,2).join(' ')).join(' → ') || 'Ground';
              if (r.type === 'car') return r.carCompany || 'Car Rental';
              if (r.type === 'train') return `${r.trainFrom||'?'} → ${r.trainTo||'?'}`;
              return '';
            };
            return (
              <div style={{ display:'flex', justifyContent:'center', gap:8, padding:'0 16px 12px', flexWrap:'wrap' }}>
                {Object.values(groups).map(recs => {
                  const r = recs[0];
                  const count = recs.length;
                  const names = (r.passenger||'').split(',').map(s=>s.trim()).filter(Boolean);
                  const colors = names.map(n => resolveColor(n)).filter(Boolean);
                  const c = colors[0] || typeColors[r.type] || '#1E40AF';
                  const summary = count > 1 ? `${count} ${r.type === 'flight' ? 'flights' : 'records'}` : getSummary(r);
                  return (
                    <button key={r.id} onClick={() => setTravelModal(count > 1 ? recs : r)}
                      style={{ minWidth:90, padding:'10px 12px', borderRadius:14, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4,
                        background: colors.length > 1
                          ? `linear-gradient(135deg, ${colors[0]}22, ${colors[1]}22)`
                          : `${c}14`,
                        border: `1.5px solid ${c}33`,
                        cursor:'pointer', transition:'all 0.15s', position:'relative', fontFamily:'inherit' }}>
                      {count > 1 && (
                        <div style={{ position:'absolute', top:-6, right:-6, minWidth:18, height:18, borderRadius:9,
                          background:c, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 4px',
                          boxShadow:'0 2px 6px rgba(0,0,0,0.3)' }}>
                          <span style={{ fontSize:10, fontWeight:800, color:'#fff', lineHeight:1 }}>{count}</span>
                        </div>
                      )}
                      <i className={`fa-solid ${typeIcons[r.type] || 'fa-circle'}`} style={{ fontSize:20, color:c }}></i>
                      {summary && (
                        <span style={{ fontSize:9, fontWeight:800, color:c, letterSpacing:0.3, lineHeight:1, maxWidth:100, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {summary}
                        </span>
                      )}
                      {names.length > 0 && (
                        <div style={{ display:'flex', gap:2, alignItems:'center' }}>
                          {names.map((n, i) => {
                            const nc = resolveColor(n) || 'var(--text3)';
                            return <span key={i} style={{ fontSize:7, fontWeight:800, color: nc, letterSpacing:0.3, lineHeight:1 }}>
                              {n.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)}{i < names.length-1 ? '+' : ''}
                            </span>;
                          })}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {renderNotes(fullSheet.notes)}
          {(fullSheet.hotels||[]).length > 0 && (
            <div style={{ padding:'0 16px 12px' }}>
              <LodgingPanel hotels={fullSheet.hotels} />
            </div>
          )}
          {renderSchedule(fullSheet.events || [], renderPersonLegend(fullSheet.events || []), fullSheet)}

          {/* ── Files ── */}
          {(() => {
            const allFiles = [];
            (fullSheet.events||[]).forEach(e => {
              (e.files||[]).forEach(f => { allFiles.push({ ...f, eventTitle: e.title }); });
            });
            if (!allFiles.length) return null;
            const openFile = (f) => {
              if (f.url) { window.open((f.url.startsWith('http') ? '' : (process.env.REACT_APP_API_URL||'http://localhost:3001')) + f.url, '_blank'); return; }
              if (f.data) {
                const arr = f.data.split(','); const mime = arr[0].match(/:(.*?);/)[1];
                const bstr = atob(arr[1]); const u8 = new Uint8Array(bstr.length);
                for (let i = 0; i < bstr.length; i++) u8[i] = bstr.charCodeAt(i);
                window.open(URL.createObjectURL(new Blob([u8], { type: mime })), '_blank');
              }
            };
            return (
              <div style={{ margin:'0 16px 12px', background:'var(--card)', borderRadius:16, border:'1px solid var(--border)', overflow:'hidden' }}>
                <div style={{ padding:'14px 16px 10px', borderBottom:'1px solid var(--border)' }}>
                  <p style={{ fontSize:10, fontWeight:700, letterSpacing:3, color:'var(--text3)', margin:0 }}>
                    <i className="fa-solid fa-paperclip" style={{ marginRight:5, fontSize:9 }}></i>
                    FILES
                  </p>
                </div>
                <div style={{ padding:'12px 16px', display:'flex', gap:10, overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
                  {allFiles.map((f, i) => {
                    const isImage = f.type?.startsWith('image/') || f.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                    return (
                      <div key={i} onClick={() => openFile(f)}
                        style={{ flexShrink:0, width:100, cursor:'pointer', borderRadius:10, overflow:'hidden', border:'1px solid var(--border)', background:'var(--bg)' }}>
                        <div style={{ width:'100%', height:72, background:'var(--bg2)', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
                          {isImage && (f.data || f.url)
                            ? <img src={f.data || f.url} alt={f.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                            : <i className={`fa-solid ${f.type?.includes('pdf') ? 'fa-file-pdf' : 'fa-file'}`} style={{ fontSize:24, color:'var(--text3)' }}></i>
                          }
                        </div>
                        <div style={{ padding:'6px 8px' }}>
                          <p style={{ fontSize:10, fontWeight:700, color:'var(--text)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {f.name?.replace(/\.[^.]+$/, '') || 'File'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ── Travel ── */}
          {(fullSheet.travel||[]).length > 0 && (
            <div style={{ margin:'0 16px 12px', background:'var(--card)', borderRadius:16, border:'1px solid var(--border)', overflow:'hidden' }}>
              <div style={{ padding:'14px 16px 10px', borderBottom:'1px solid var(--border)' }}>
                <p style={{ fontSize:10, fontWeight:700, letterSpacing:3, color:'var(--text3)', margin:0 }}>
                  <i className="fa-solid fa-plane" style={{ marginRight:5, fontSize:9 }}></i>
                  TRAVEL
                </p>
              </div>
              <div style={{ padding:'10px 12px', display:'flex', flexDirection:'column', gap:6 }}>
                {(fullSheet.travel||[]).map(r => {
                  const hero = getTravelHero(r);
                  const icons = { flight:'fa-plane', hotel:'fa-hotel', carservice:'fa-car-side', car:'fa-car', ground:'fa-van-shuttle', train:'fa-train' };
                  const typeLabel = { flight:'Flight', hotel:'Hotel', carservice:'Car Service', car:'Car Rental', ground:'Ground', train:'Train' }[r.type];
                  const name = r.type==='flight'?(r.airline?r.airline+' ':'')+(r.flightNumber||''):r.type==='carservice'?r.carServiceCompany||'Car Service':r.hotelName||r.carCompany||r.driverName||r.trainCompany;
                  const title = name ? `${typeLabel}: ${name}` : typeLabel;
                  const address = r.hotelAddress||r.carPickupLocation||r.pickupLocation||r.trainFrom;
                  const confirmation = r.hotelConfirmation||r.carConfirmation||r.trainConfirmation;
                  return (
                    <div key={r.id} onClick={() => setTravelModal(r)}
                      style={{ borderRadius:12, border:'1px solid var(--border)', cursor:'pointer', overflow:'hidden', transition:'background 0.15s' }}>
                      {/* Hero thumbnail */}
                      {hero.imageUrl ? (
                        <div style={{ height:80, backgroundImage:`url(${hero.imageUrl})`, backgroundSize:'cover', backgroundPosition:'center', position:'relative' }}>
                          <div style={{ position:'absolute', inset:0, background:'linear-gradient(transparent 30%, var(--bg) 100%)' }} />
                          <div style={{ position:'absolute', bottom:8, left:12, display:'flex', alignItems:'center', gap:6 }}>
                            <i className={`fa-solid ${icons[r.type]}`} style={{ fontSize:11, color:'white' }}></i>
                            <span style={{ fontSize:11, fontWeight:700, color:'white', textShadow:'0 1px 3px rgba(0,0,0,0.5)' }}>{typeLabel}</span>
                          </div>
                        </div>
                      ) : (
                        <div style={{ height:48, background:hero.gradient, position:'relative', display:'flex', alignItems:'center', padding:'0 12px', gap:6 }}>
                          <i className={`fa-solid ${icons[r.type]}`} style={{ fontSize:11, color:'rgba(255,255,255,0.7)' }}></i>
                          <span style={{ fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.8)' }}>{typeLabel}</span>
                        </div>
                      )}
                      <div style={{ padding:'10px 14px', background:'var(--bg)' }}>
                        <p style={{ fontSize:14, fontWeight:800, color:'var(--text)', margin:0 }}>{title}</p>
                        {address && (
                          <p style={{ fontSize:12, color:'var(--text3)', margin:'4px 0 0' }}>
                            <i className="fa-solid fa-location-dot" style={{ fontSize:9, marginRight:4 }}></i>{address.length > 45 ? address.substring(0,45)+'...' : address}
                          </p>
                        )}
                        {(r.greeterName || r.greeterPhone) && (
                          <p style={{ fontSize:11, color:'var(--text2)', marginTop:3 }}>
                            <i className="fa-solid fa-hand-wave" style={{ fontSize:9, color:'#fbbf24', marginRight:3 }}></i>
                            {r.greeterName||''}{r.greeterPhone ? ' · '+r.greeterPhone : ''}
                          </p>
                        )}
                        {confirmation && <p style={{ fontSize:11, color:'var(--text3)', marginTop:2, fontFamily:'monospace' }}>#{confirmation}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>

      ) : initialized ? (
        <div style={{ textAlign:'center', padding:'80px 24px' }}>
          <i className="fa-regular fa-calendar-xmark" style={{ fontSize:40, color:'var(--text3)', marginBottom:16, display:'block' }}></i>
          <p style={{ fontSize:18, fontWeight:800, color:'var(--text)', marginBottom:6 }}>No sheet for today</p>
          <p style={{ fontSize:13, color:'var(--text3)' }}>Check back later</p>
        </div>
      ) : null}

      {/* ── Travel modal (shared component with smart hero) ── */}
      <TravelModal record={travelModal} onClose={() => setTravelModal(null)} />

      {/* ── Event modal overlay ── */}
      {eventModal && (
        <EventModal
          event={eventModal.event}
          sheet={eventModal.sheet}
          prevEvent={eventModal.prevEvent}
          nextEvent={eventModal.nextEvent}
          colorMap={colorMap}
          onClose={() => setEventModal(null)}
        />
      )}
    </div>
  );
}
